import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { listExperiments, upsertExperiment } from "@/lib/experiments.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { Plus, FlaskConical } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/experiments")({
  head: () => ({ meta: [{ title: "Experimentos — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ExperimentsPage,
});

type Variant = { key: string; name: string; weight: number; is_control: boolean; payload: Record<string, unknown> };
type Exp = {
  id?: string; key: string; name: string; description?: string | null; hypothesis?: string | null;
  status: "draft" | "running" | "paused" | "ended"; primary_metric?: string | null; variants: Variant[];
};

function blank(): Exp {
  return {
    key: "", name: "", description: "", hypothesis: "", status: "draft", primary_metric: "checkout_completed",
    variants: [
      { key: "control", name: "Controle", weight: 50, is_control: true, payload: {} },
      { key: "variant_a", name: "Variante A", weight: 50, is_control: false, payload: {} },
    ],
  };
}

function ExperimentsPage() {
  const listFn = useServerFn(listExperiments);
  const upsertFn = useServerFn(upsertExperiment);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-exps"], queryFn: () => listFn() });
  const [editing, setEditing] = useState<Exp | null>(null);

  const save = useMutation({
    mutationFn: (e: Exp) => upsertFn({ data: e as never }),
    onSuccess: () => { toast.success("Experimento salvo"); qc.invalidateQueries({ queryKey: ["admin-exps"] }); setEditing(null); },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao salvar"),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <PageHeader title="Experimentos A/B" description="Teste headlines, CTAs e ordem dos blocos com atribuição consistente." />
        <button
          onClick={() => setEditing(blank())}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--navy)]/90"
        >
          <Plus size={12} /> Novo experimento
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {(q.data ?? []).map((r: any) => (
          <div key={r.experiment.id} className="rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="flex items-center gap-2 font-display text-base font-extrabold">
                  <FlaskConical size={14} /> {r.experiment.name}
                </p>
                <p className="mt-0.5 font-mono text-[11px] text-[var(--gray-ink)]">{r.experiment.key}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                r.experiment.status === "running" ? "bg-[var(--green)]/10 text-[var(--green)]"
                : r.experiment.status === "ended" ? "bg-black/10 text-black/60"
                : "bg-[var(--gold)]/10 text-[var(--gold)]"
              }`}>{r.experiment.status}</span>
            </div>
            {r.experiment.hypothesis && (
              <p className="mt-2 text-xs italic text-[var(--gray-ink)]">"{r.experiment.hypothesis}"</p>
            )}
            <table className="mt-3 w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
                  <th className="pb-1">Variante</th><th className="pb-1 text-right">Amostra</th>
                  <th className="pb-1 text-right">Conv.</th><th className="pb-1 text-right">Taxa</th>
                  <th className="pb-1 text-right">Tempo méd.</th>
                </tr>
              </thead>
              <tbody>
                {r.totals.map((t: any) => (
                  <tr key={t.variant} className="border-t border-black/5">
                    <td className="py-1.5 font-semibold">{t.variant}</td>
                    <td className="py-1.5 text-right">{t.assigned}</td>
                    <td className="py-1.5 text-right">{t.converted}</td>
                    <td className="py-1.5 text-right font-bold text-[var(--navy)]">{t.convRate}%</td>
                    <td className="py-1.5 text-right text-[var(--gray-ink)]">{t.avgSeconds != null ? `${t.avgSeconds}s` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={() => setEditing({
                id: r.experiment.id, key: r.experiment.key, name: r.experiment.name,
                description: r.experiment.description, hypothesis: r.experiment.hypothesis,
                status: r.experiment.status, primary_metric: r.experiment.primary_metric,
                variants: r.variants.map((v: any) => ({
                  key: v.key, name: v.name, weight: v.weight, is_control: v.is_control, payload: v.payload ?? {},
                })),
              })}
              className="mt-3 text-xs font-semibold text-[var(--navy)] hover:underline"
            >Editar</button>
          </div>
        ))}
        {(q.data ?? []).length === 0 && (
          <p className="rounded-xl border border-dashed border-black/10 p-6 text-center text-sm text-[var(--gray-ink)]">
            Nenhum experimento configurado. Comece criando um teste da headline da landing.
          </p>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-auto">
          <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="mb-4 font-display text-lg font-extrabold">{editing.id ? "Editar experimento" : "Novo experimento"}</h3>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold">Chave</span>
                <input value={editing.key} disabled={!!editing.id}
                  onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 font-mono text-xs" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Nome</span>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-semibold">Hipótese</span>
                <input value={editing.hypothesis ?? ""} onChange={(e) => setEditing({ ...editing, hypothesis: e.target.value })}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Status</span>
                <select value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value as never })}
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2">
                  <option value="draft">Rascunho</option><option value="running">Rodando</option>
                  <option value="paused">Pausado</option><option value="ended">Encerrado</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Métrica primária</span>
                <input value={editing.primary_metric ?? ""} onChange={(e) => setEditing({ ...editing, primary_metric: e.target.value })}
                  placeholder="ex: checkout_completed"
                  className="mt-1 w-full rounded-md border border-black/10 px-3 py-2 font-mono text-xs" />
              </label>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">Variantes</p>
              <div className="space-y-2">
                {editing.variants.map((v, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input placeholder="chave" value={v.key}
                      onChange={(e) => {
                        const arr = [...editing.variants];
                        arr[i] = { ...v, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") };
                        setEditing({ ...editing, variants: arr });
                      }}
                      className="col-span-3 rounded-md border border-black/10 px-2 py-1.5 font-mono text-xs" />
                    <input placeholder="nome" value={v.name}
                      onChange={(e) => { const arr = [...editing.variants]; arr[i] = { ...v, name: e.target.value }; setEditing({ ...editing, variants: arr }); }}
                      className="col-span-5 rounded-md border border-black/10 px-2 py-1.5 text-xs" />
                    <input type="number" min={0} placeholder="peso" value={v.weight}
                      onChange={(e) => { const arr = [...editing.variants]; arr[i] = { ...v, weight: Math.max(0, Number(e.target.value)) }; setEditing({ ...editing, variants: arr }); }}
                      className="col-span-2 rounded-md border border-black/10 px-2 py-1.5 text-xs" />
                    <label className="col-span-2 inline-flex items-center gap-1 text-[11px]">
                      <input type="checkbox" checked={v.is_control}
                        onChange={(e) => { const arr = editing.variants.map((x, j) => ({ ...x, is_control: j === i ? e.target.checked : false })); setEditing({ ...editing, variants: arr }); }} />
                      controle
                    </label>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setEditing({ ...editing, variants: [...editing.variants, { key: `var_${editing.variants.length}`, name: "Nova variante", weight: 50, is_control: false, payload: {} }] })}
                  className="text-xs font-semibold text-[var(--navy)] hover:underline"
                  disabled={editing.variants.length >= 6}
                >+ Adicionar variante</button>
                {editing.variants.length > 2 && (
                  <button
                    onClick={() => setEditing({ ...editing, variants: editing.variants.slice(0, -1) })}
                    className="text-xs font-semibold text-red-600 hover:underline"
                  >− Remover última</button>
                )}
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-lg px-3 py-2 text-sm font-semibold hover:bg-[var(--gray-soft)]">Cancelar</button>
              <button onClick={() => save.mutate(editing)} disabled={save.isPending || !editing.key || !editing.name}
                className="rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {save.isPending ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
